import { CartAddEvent, CartErrorEvent } from '@theme/events';

class GGProjectMaterialsCalculator extends HTMLElement {
  connectedCallback() {
    if (this.dataset.initialized === 'true') return;
    if (!window.GoGreenMaterialsCalculator) {
      document.addEventListener('gg:materials-calculator:ready', this.handleCoreReady, { once: true });
      return;
    }
    this.dataset.initialized = 'true';

    this.lengthInput = this.querySelector('[data-project-length]');
    this.widthInput = this.querySelector('[data-project-width]');
    this.sqftOutput = this.querySelector('[data-project-sqft-output]');
    this.seamsOutput = this.querySelector('[data-estimated-seams-output]');
    this.rows = Array.from(this.querySelectorAll('[data-material-row]'));
    this.addButton = this.querySelector('[data-add-materials]');
    this.totalCost = this.querySelector('[data-materials-total-cost]');
    this.status = this.querySelector('[data-materials-status]');
    this.core = window.GoGreenMaterialsCalculator;
    this.core?.registerFromDocument?.(this);

    this.lengthInput?.addEventListener('input', this.handleInput);
    this.widthInput?.addEventListener('input', this.handleInput);
    for (const row of this.rows) {
      row.querySelector('[data-material-enabled]')?.addEventListener('change', this.handleInput);
      row.querySelector('[data-material-select]')?.addEventListener('change', this.handleInput);
      row.querySelector('[data-material-quantity]')?.addEventListener('input', this.handleQuantityInput);
    }
    this.addButton?.addEventListener('click', this.handleAddToCart);

    this.updateRows();
  }

  disconnectedCallback() {
    document.removeEventListener('gg:materials-calculator:ready', this.handleCoreReady);
    this.lengthInput?.removeEventListener('input', this.handleInput);
    this.widthInput?.removeEventListener('input', this.handleInput);
    for (const row of this.rows) {
      row.querySelector('[data-material-enabled]')?.removeEventListener('change', this.handleInput);
      row.querySelector('[data-material-select]')?.removeEventListener('change', this.handleInput);
      row.querySelector('[data-material-quantity]')?.removeEventListener('input', this.handleQuantityInput);
    }
    this.addButton?.removeEventListener('click', this.handleAddToCart);
  }

  handleInput = () => {
    this.updateRows();
  };

  handleCoreReady = () => {
    this.connectedCallback();
  };

  handleQuantityInput = () => {
    this.updateButtonState();
  };

  parseNumber(value) {
    const number = parseFloat(String(value || '').replace(/,/g, ''));
    return Number.isFinite(number) ? number : 0;
  }

  getProjectLength() {
    return Math.max(0, this.parseNumber(this.lengthInput?.value));
  }

  getProjectWidth() {
    return Math.max(0, this.parseNumber(this.widthInput?.value));
  }

  getProjectSqft() {
    return this.getProjectLength() * this.getProjectWidth();
  }

  getTurfRollWidth() {
    return this.parseNumber(this.dataset.turfRollWidth);
  }

  getTurfRequiresInfill() {
    return this.dataset.turfRequiresInfill === 'true';
  }

  getTurfInfillLbsPerSqft() {
    return this.parseNumber(this.dataset.turfInfillLbsPerSqft);
  }

  getSelectedOption(row) {
    const select = row.querySelector('[data-material-select]');
    if (!(select instanceof HTMLSelectElement)) return null;
    return select.options[select.selectedIndex] || null;
  }

  getCoverage(row) {
    const coverage = this.parseNumber(this.getSelectedOption(row)?.dataset.coverage);
    return coverage > 0 ? coverage : 0;
  }

  getOptionPrice(option) {
    const price = this.parseNumber(option?.dataset.price);
    return price > 0 ? price : 0;
  }

  formatPrice(price) {
    const moneyFormat = this.dataset.moneyFormat || '${{amount}}';
    const value = this.parseNumber(price) / 100;
    const amount = value.toLocaleString(undefined, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });

    return moneyFormat.replace(/{{\s*amount\s*}}/g, amount);
  }

  calculateSeams() {
    const result = this.core?.calculateSeams?.({
      length: this.getProjectLength(),
      width: this.getProjectWidth(),
      rollWidth: this.getTurfRollWidth(),
    });

    return {
      projectSqft: result?.squareFeet || 0,
      estimatedSeamLength: result?.estimatedSeamLinearFeet || 0,
      missingRollWidth: result?.missingRollWidth || false,
    };
  }

  setRowWarning(row, message) {
    const warning = row.querySelector('[data-material-warning]');
    if (warning) warning.textContent = message || '';
  }

  calculateRowQuantity(row, calculation) {
    const type = row.dataset.materialType;
    const option = this.getSelectedOption(row);
    if (!option?.value || !this.core) return { quantity: 0, warning: '' };

    const material = {
      kind: type === 'seam-tape' ? 'seam_tape' : type,
      productId: option.dataset.productId || '',
      variantId: option.value,
      sku: option.dataset.sku || '',
      label: option.dataset.label || option.dataset.productTitle || option.textContent?.trim() || '',
      price: option.dataset.price,
      available: option.dataset.available !== 'false',
      coverageLinearFeet: option.dataset.coverage,
      weight: option.dataset.weight,
      weightUnit: option.dataset.weightUnit,
    };
    const result = this.core.calculate({
      squareFeet: calculation.projectSqft,
      length: this.getProjectLength(),
      width: this.getProjectWidth(),
      estimatedSeamLinearFeet: calculation.estimatedSeamLength,
      config: {
        productId: this.dataset.productId,
        variantId: this.dataset.variantId,
        rollWidthFeet: this.getTurfRollWidth(),
        requiresInfill: this.getTurfRequiresInfill(),
        infillLbsPerSqft: this.getTurfInfillLbsPerSqft(),
        materials: [material],
      },
    });
    const recommendation = result.recommendations[0];
    let warning = recommendation?.warning || '';

    if (type === 'infill' && !warning) {
      const recommendedInfillId = this.dataset.recommendedInfillId;
      const recommendedInfillType = this.dataset.recommendedInfillType;
      if (recommendedInfillType && option.dataset.infillType && recommendedInfillType !== option.dataset.infillType) {
        warning = `Selected infill type does not match recommended type: ${recommendedInfillType}.`;
      } else if (recommendedInfillId && option.dataset.productId && recommendedInfillId !== option.dataset.productId) {
        warning = `Selected infill does not match ${this.dataset.recommendedInfillTitle || 'the recommended infill'}.`;
      }
    }

    return { quantity: recommendation?.quantity || 0, warning };
  }

  updateRows() {
    const calculation = this.calculateSeams();

    if (this.sqftOutput) this.sqftOutput.textContent = Math.ceil(calculation.projectSqft).toString();
    if (this.seamsOutput) this.seamsOutput.textContent = `${calculation.estimatedSeamLength} LF`;

    for (const row of this.rows) {
      const enabled = row.querySelector('[data-material-enabled]');
      const quantity = row.querySelector('[data-material-quantity]');
      const select = row.querySelector('[data-material-select]');
      const lineTotal = row.querySelector('[data-material-line-total]');
      const isEnabled = !(enabled instanceof HTMLInputElement) || enabled.checked;
      const result = isEnabled ? this.calculateRowQuantity(row, calculation) : { quantity: 0, warning: '' };
      row.classList.toggle('gg-materials-calculator__row--disabled', !isEnabled);
      if (quantity instanceof HTMLInputElement) {
        quantity.value = result.quantity.toString();
        quantity.disabled = !isEnabled;
      }
      if (select instanceof HTMLSelectElement) select.disabled = !isEnabled;
      if (lineTotal) lineTotal.textContent = this.formatPrice(0);
      this.setRowWarning(row, isEnabled ? result.warning : '');
    }

    this.updateButtonState();
  }

  updateButtonState() {
    const lineItems = this.getLineItems();
    const hasEnabledProduct = lineItems.length > 0;

    if (this.addButton instanceof HTMLButtonElement) {
      this.addButton.disabled = !hasEnabledProduct;
    }
    for (const row of this.rows) {
      const lineTotal = row.querySelector('[data-material-line-total]');
      const enabled = row.querySelector('[data-material-enabled]');
      const quantity = row.querySelector('[data-material-quantity]');
      const option = this.getSelectedOption(row);
      const isEnabled = !(enabled instanceof HTMLInputElement) || enabled.checked;
      const itemQuantity = Math.max(0, Math.ceil(Number(quantity?.value) || 0));
      const price = this.getOptionPrice(option);

      if (lineTotal) lineTotal.textContent = this.formatPrice(isEnabled ? price * itemQuantity : 0);
    }
    if (this.totalCost) {
      this.totalCost.textContent = this.formatPrice(
        lineItems.reduce((sum, item) => sum + item.price * item.quantity, 0)
      );
    }
  }

  getLineItems() {
    const items = [];

    for (const row of this.rows) {
      const enabled = row.querySelector('[data-material-enabled]');
      const quantity = row.querySelector('[data-material-quantity]');
      const option = this.getSelectedOption(row);
      const isEnabled = !(enabled instanceof HTMLInputElement) || enabled.checked;
      const variantId = Number(option?.value);
      const itemQuantity = Math.max(0, Math.ceil(Number(quantity?.value) || 0));
      const price = this.getOptionPrice(option);

      if (isEnabled && variantId > 0 && itemQuantity > 0) {
        items.push({ id: variantId, quantity: itemQuantity, price });
      }
    }

    return items;
  }

  setStatus(message) {
    if (this.status) this.status.textContent = message;
  }

  handleAddToCart = async () => {
    const items = this.getLineItems();
    if (!items.length) return;

    const button = this.addButton;
    if (button instanceof HTMLButtonElement) button.disabled = true;
    this.setStatus('');

    const sectionIds = Array.from(document.querySelectorAll('cart-items-component'))
      .map((item) => item instanceof HTMLElement ? item.dataset.sectionId : '')
      .filter(Boolean);

    try {
      const response = await fetch(Theme.routes.cart_add_url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify({
          items: items.map(({ id, quantity }) => ({ id, quantity })),
          sections: sectionIds.join(','),
          sections_url: window.location.pathname,
        }),
      });
      const result = await response.json();

      if (!response.ok || result.status) {
        const message = result.description || result.message || 'Unable to add materials to cart.';
        this.dispatchEvent(new CartErrorEvent(this.id, message, result.description, result.errors));
        this.setStatus(message);
        return;
      }

      const cart = await fetch(Theme.routes.cart_url + '.js').then((cartResponse) => cartResponse.json());
      this.dispatchEvent(
        new CartAddEvent(cart, this.id, {
          source: 'gg-project-materials-calculator',
          itemCount: items.reduce((sum, item) => sum + item.quantity, 0),
          sections: result.sections,
        })
      );

      const popup = this.closest('dialog-component');
      await popup?.closeDialog?.();

      const cartDrawer = document.querySelector('cart-drawer-component');
      if (cartDrawer?.open) {
        cartDrawer.open();
      } else {
        document.querySelector('[data-testid="cart-drawer-trigger"]')?.click();
      }
    } catch (error) {
      console.error(error);
      this.setStatus('Unable to add materials to cart.');
    } finally {
      this.updateRows();
    }
  };
}

if (!customElements.get('gg-project-materials-calculator')) {
  customElements.define('gg-project-materials-calculator', GGProjectMaterialsCalculator);
}
